/**
 * Branded month-grid Calendar — the single reusable date surface for both
 * consoles. Plain ISO strings ('YYYY-MM-DD') in and out, so there is zero
 * timezone math (string compare is correct for ISO dates).
 *
 * Three self-contained views (no nested portals): the day grid, a year grid,
 * and a month grid. Tapping the caption drills out to year → month → day, which
 * keeps it usable for far-back dates (birthdates) as well as near-term ones
 * (bookings, blackouts). Driven by `<DatePicker>`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const IST = 'Asia/Kolkata';
const todayIso = () => new Date().toLocaleDateString('en-CA', { timeZone: IST }); // YYYY-MM-DD
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

export interface CalendarProps {
  value?: string;
  onSelect: (value: string) => void;
  min?: string;
  max?: string;
  fromYear?: number;
  toYear?: number;
  className?: string;
}

export function Calendar({ value, onSelect, min, max, fromYear, toYear, className }: CalendarProps) {
  const today = todayIso();
  const todayY = Number(today.slice(0, 4));

  const minYear = fromYear ?? (min ? Number(min.slice(0, 4)) : todayY - 100);
  const maxYear = toYear ?? (max ? Number(max.slice(0, 4)) : todayY + 5);

  // The month being shown. Seed from the value, else today, clamped to range.
  const seed = value && value.length === 10 ? value : today;
  const [view, setView] = useState(() => {
    const y = Math.min(Math.max(Number(seed.slice(0, 4)), minYear), maxYear);
    return { year: y, month: Number(seed.slice(5, 7)) - 1 };
  });
  const [mode, setMode] = useState<'days' | 'years' | 'months'>('days');

  const firstWeekday = new Date(view.year, view.month, 1).getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const cells = useMemo(() => {
    const out: (number | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(d);
    return out;
  }, [firstWeekday, daysInMonth]);

  const years = useMemo(() => {
    const out: number[] = [];
    for (let y = maxYear; y >= minYear; y--) out.push(y);
    return out;
  }, [minYear, maxYear]);

  const atFirstMonth = view.year <= minYear && view.month <= 0;
  const atLastMonth = view.year >= maxYear && view.month >= 11;

  function step(delta: number) {
    setView((v) => {
      let m = v.month + delta;
      let y = v.year;
      if (m < 0) { m = 11; y -= 1; }
      if (m > 11) { m = 0; y += 1; }
      return { year: Math.min(Math.max(y, minYear), maxYear), month: m };
    });
  }

  const disabled = (d: number) => {
    const cell = iso(view.year, view.month, d);
    if (min && cell < min) return true;
    if (max && cell > max) return true;
    return false;
  };

  // Scroll the selected year into view when the year grid opens.
  const yearGridRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mode !== 'years') return;
    yearGridRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'center' });
  }, [mode]);

  return (
    <div className={cn('w-[17.5rem] select-none p-3', className)} data-testid="calendar">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => (mode === 'days' ? step(-1) : setView((v) => ({ ...v, year: Math.max(v.year - 1, minYear) })))}
          disabled={mode === 'days' ? atFirstMonth : view.year <= minYear}
          className="flex h-8 w-8 items-center justify-center rounded-button text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
          aria-label="Previous"
          data-testid="calendar-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <button
          type="button"
          onClick={() => setMode((m) => (m === 'days' ? 'years' : 'days'))}
          className="rounded-button px-3 py-1 font-heading text-sm font-bold text-foreground transition-colors hover:bg-secondary"
          data-testid="calendar-caption"
        >
          {mode === 'days' ? `${MONTHS[view.month]} ${view.year}` : mode === 'months' ? view.year : 'Pick a year'}
        </button>

        <button
          type="button"
          onClick={() => (mode === 'days' ? step(1) : setView((v) => ({ ...v, year: Math.min(v.year + 1, maxYear) })))}
          disabled={mode === 'days' ? atLastMonth : view.year >= maxYear}
          className="flex h-8 w-8 items-center justify-center rounded-button text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
          aria-label="Next"
          data-testid="calendar-next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Day grid */}
      {mode === 'days' && (
        <>
          <div className="mb-1 grid grid-cols-7">
            {WEEKDAYS.map((w) => (
              <div key={w} className="py-1 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {w}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (d === null) return <div key={`e${i}`} />;
              const cell = iso(view.year, view.month, d);
              const isSelected = cell === value;
              const isToday = cell === today;
              const isDisabled = disabled(d);
              return (
                <button
                  key={cell}
                  type="button"
                  disabled={isDisabled}
                  onClick={() => onSelect(cell)}
                  data-testid={`day-${cell}`}
                  aria-pressed={isSelected}
                  className={cn(
                    'flex h-9 items-center justify-center rounded-button text-sm font-medium tabular-nums transition-colors duration-fast',
                    isSelected
                      ? 'bg-primary text-primary-foreground shadow-clay-sm'
                      : 'text-foreground hover:bg-secondary',
                    !isSelected && isToday && 'ring-2 ring-inset ring-primary/40',
                    'disabled:pointer-events-none disabled:text-muted-foreground/40',
                  )}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </>
      )}

      {/* Year grid */}
      {mode === 'years' && (
        <div ref={yearGridRef} className="grid max-h-[15.5rem] grid-cols-4 gap-1 overflow-y-auto p-0.5">
          {years.map((y) => (
            <button
              key={y}
              type="button"
              data-active={y === view.year}
              data-testid={`cal-year-${y}`}
              onClick={() => {
                setView((v) => ({ ...v, year: y }));
                setMode('months');
              }}
              className={cn(
                'rounded-button py-2 text-sm font-medium tabular-nums transition-colors',
                y === view.year ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-secondary',
              )}
            >
              {y}
            </button>
          ))}
        </div>
      )}

      {/* Month grid */}
      {mode === 'months' && (
        <div className="grid grid-cols-3 gap-1 p-0.5">
          {MONTHS_SHORT.map((m, i) => (
            <button
              key={m}
              type="button"
              data-testid={`cal-month-${i}`}
              onClick={() => {
                setView((v) => ({ ...v, month: i }));
                setMode('days');
              }}
              className={cn(
                'rounded-button py-2.5 text-sm font-medium transition-colors',
                i === view.month ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-secondary',
              )}
            >
              {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
